/**
 * Demo mode — the whole app running on sample data alone, no backend, no
 * sign-in. Built only when `VITE_DEMO=1` (`npm run build:demo`); every other
 * build leaves `window.fetch` untouched.
 *
 * Works by answering the app's own network calls here, so every screen runs
 * its real code path: list endpoints come back empty and the app's existing
 * sample merge (samples.js) fills them, sample records answer from samples.js
 * as always, and writes "succeed" with a notice. A little state lives in
 * memory — who's marked away, the daily-chase list, the accounts team — so those
 * screens move when clicked. A reload resets everything.
 */
import { sampleToast } from './lib/sampleFiles'
import { sampleScreenData } from './lib/samples'

export const DEMO = import.meta.env.VITE_DEMO === '1'

const USER = { _id: 'demo-user', username: 'demo', name: 'Demo User', token: 'demo-token' }

const ACCOUNTANTS = [
  { name: 'Reshu Singh', email: 'reshu.singh@binnys.in', visionUsername: 'reshu', routesCategory: 'BILLING', zuid: '60035175429', kekaEmployeeId: 'K1', backupEmails: ['nirvi.upadhyay@binnys.in'] },
  { name: 'Paresh Tandle', email: 'paresh.tandle@binnys.in', visionUsername: 'paresh', routesCategory: 'TDS', zuid: '60071884362', kekaEmployeeId: 'K2', backupEmails: ['nirvi.upadhyay@binnys.in'] },
  { name: 'Supriya Mirgal', email: 'supriya.mirgal@binnys.in', visionUsername: 'supriya', routesCategory: 'CASH_BANK', zuid: '60024607742', kekaEmployeeId: 'K3', backupEmails: ['aarti.pipaliya@binnys.in'] },
  { name: 'Nirvi Upadhyay', email: 'nirvi.upadhyay@binnys.in', visionUsername: 'nirvi', routesCategory: 'OTHER', zuid: '60059507943', kekaEmployeeId: 'K4', backupEmails: [] },
  { name: 'Aarti Pipaliya', email: 'aarti.pipaliya@binnys.in', visionUsername: 'aarti', routesCategory: null, zuid: '60024567687', kekaEmployeeId: null, backupEmails: [], getsEscalations: true },
].map((a) => ({ ...a, active: true, awaySince: null, awayMarkedBy: null, lastNudgeAt: new Date(Date.now() - 86_400_000).toISOString(), nudgeCount: 4 }))

/** The directory as the backend sends it — with the first available backup's name filled in. */
const directory = ({ all = false } = {}) =>
  ACCOUNTANTS.filter((a) => all || a.active).map((a) => {
    const cover = a.backupEmails.map((e) => ACCOUNTANTS.find((x) => x.email === e)).find((x) => x?.active && !x.awaySince)
    return { ...a, backupEmail: cover?.email ?? null, backupName: cover?.name ?? null }
  })

const state = { autoChase: new Set(sampleScreenData().autoChaseCodes) }

const respond = (data, status = 200, message = null) =>
  new Response(JSON.stringify({ status, data, ...(message ? { message } : {}) }), { status, headers: { 'Content-Type': 'application/json' } })

const body = (init) => {
  try {
    return typeof init?.body === 'string' ? JSON.parse(init.body) : {}
  } catch {
    return {}
  }
}

function handle(url, init = {}) {
  const u = new URL(url, location.href)
  const method = (init.method ?? 'GET').toUpperCase()
  const path = u.pathname.replace(/^.*\/api\/v1/, '')

  // Vision sign-in — any username and password works in the demo.
  if (/\/users\/(login|me)$/.test(u.pathname)) return respond(USER)
  if (/\/users\/logout$/.test(u.pathname)) return respond(null)
  // Our own side of the ledger, from Track — the samples are appended by ledgerApi.js.
  if (/trackApp\/ledger/.test(u.pathname)) return respond({ ledgerData: [], totalClients: 0, page: 1, limit: 1000 })

  if (method === 'GET') {
    if (path === '/me') return respond({ username: USER.username, isAdmin: true, accountant: { name: USER.name, email: 'demo@binnys.in' } })
    if (path === '/ledger/requests') return respond({ requests: [], summary: {} })
    if (path === '/ledger/clients/screen-data')
      return respond({ collectibleSubCategories: [], bySubCategory: {}, clientCodes: [], total: 0, contacts: {}, autoChaseCodes: [...state.autoChase] })
    if (path === '/ledger-reconcile/tracker') return respond({ rows: [] })
    if (path === '/ledger-reconcile/runs') return respond([])
    if (path === '/ledger-reconcile/runs/latest-by-request') return respond({ runs: [] })
    if (path === '/remediation/items' || path === '/remediation/mine') return respond({ items: [] })
    if (path === '/remediation/accountants') return respond({ accountants: directory({ all: u.searchParams.get('all') === '1' }) })
    if (path === '/reports') return respond({ ledgers: [], items: [], accountants: directory().map((a) => ({ ...a, category: a.routesCategory })) })
    return respond(null, 404)
  }

  // Writes that change something on screen.
  if (path === '/ledger/clients/auto-chase') {
    const { clientCodes = [], enabled } = body(init)
    for (const c of clientCodes) enabled ? state.autoChase.add(c) : state.autoChase.delete(c)
    return respond({ matched: clientCodes.length, unreachable: [] })
  }
  if (path === '/ledger/requests/open') {
    sampleToast('Demo — period opened (simulated).')
    return respond({ opened: 0, heldForActiveCycle: [] })
  }
  let m
  if ((m = path.match(/^\/remediation\/accountants\/([^/]+)\/(away|back)$/))) {
    const a = ACCOUNTANTS.find((x) => x.email === decodeURIComponent(m[1]))
    if (a) {
      a.awaySince = m[2] === 'away' ? new Date().toISOString() : null
      a.awayMarkedBy = m[2] === 'away' ? USER.username : null
    }
    const cover = directory().find((x) => x.email === a?.email)?.backupEmail ?? null
    return respond(m[2] === 'away' ? { backup: cover, moved: cover ? 1 : 0 } : { returned: 1, keptByCover: 0 })
  }
  // Accounts Team: add, edit, deactivate, reactivate — kept in memory.
  if (path === '/remediation/accountants' && method === 'POST') {
    const b = body(init)
    const email = String(b.email ?? '').trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return respond(null, 409, 'Enter a valid email address.')
    if (ACCOUNTANTS.some((a) => a.email === email)) return respond(null, 409, 'Someone on the team already has that email.')
    if (!String(b.name ?? '').trim()) return respond(null, 409, 'Enter their name.')
    if (!String(b.visionUsername ?? '').trim()) return respond(null, 409, 'Enter their Binny username — it’s what they sign in with.')
    if (b.routesCategory) for (const a of ACCOUNTANTS) if (a.routesCategory === b.routesCategory) a.routesCategory = null
    const person = { name: b.name.trim(), email, visionUsername: b.visionUsername.trim().toLowerCase(), routesCategory: b.routesCategory || null, zuid: b.zuid || null, kekaEmployeeId: b.kekaEmployeeId || null, backupEmails: b.backupEmails ?? [], active: true, awaySince: null, nudgeCount: 0, lastNudgeAt: null }
    ACCOUNTANTS.push(person)
    return respond({ accountant: person })
  }
  if ((m = path.match(/^\/remediation\/accountants\/([^/]+)$/)) && method === 'PATCH') {
    const a = ACCOUNTANTS.find((x) => x.email === decodeURIComponent(m[1]))
    if (!a) return respond(null, 404, 'No accountant with that email.')
    const { email: _e, ...b } = body(init)
    if (b.routesCategory) for (const x of ACCOUNTANTS) if (x !== a && x.routesCategory === b.routesCategory) x.routesCategory = null
    Object.assign(a, { ...b, routesCategory: b.routesCategory || null, visionUsername: String(b.visionUsername ?? a.visionUsername).toLowerCase() })
    return respond({ accountant: a })
  }
  if ((m = path.match(/^\/remediation\/accountants\/([^/]+)\/(deactivate|reactivate)$/))) {
    const a = ACCOUNTANTS.find((x) => x.email === decodeURIComponent(m[1]))
    if (!a) return respond(null, 404, 'No accountant with that email.')
    if (m[2] === 'reactivate') {
      a.active = true
      return respond({ accountant: a })
    }
    const freed = a.routesCategory
    Object.assign(a, { active: false, routesCategory: null, backupEmails: [], awaySince: null })
    for (const x of ACCOUNTANTS) x.backupEmails = x.backupEmails.filter((e) => e !== a.email)
    const { moveTo } = body(init)
    return respond({ email: a.email, moved: 0, movedTo: moveTo ?? null, freedCategory: freed })
  }
  if (path === '/ledger-reconcile/batches') {
    sampleToast('Demo — nothing was uploaded or reconciled.')
    return respond({ results: [{ ok: false, message: 'Demo mode — reconciliation is simulated. Open a sample run below to see a result.' }] })
  }

  // Everything else: it "works", nothing leaves the browser.
  sampleToast('Demo — simulated. Nothing was sent or saved.')
  return respond({ ok: true, simulated: true })
}

if (DEMO) {
  try {
    localStorage.setItem('lereco-user', JSON.stringify(USER))
  } catch {
    /* no storage — the sign-in screen accepts anything instead */
  }
  window.fetch = async (input, init) => handle(typeof input === 'string' ? input : input.url, init)
}
